import type { FilterDropdownProps } from 'antd/es/table/interface';
import CheckboxFilterDropdown, { type CheckboxFilterOption } from './CheckboxFilterDropdown';

export type { CheckboxFilterOption };

/**
 * Build sorted, deduplicated filter options from a list of records.
 */
export function buildFilterOptions<T>(
	records: T[],
	getter: (record: T) => string | undefined,
): CheckboxFilterOption[] {
	const values = new Set<string>();
	records.forEach((record) => {
		const value = getter(record);
		if (value && value.trim()) {
			values.add(value);
		}
	});
	return Array.from(values)
		.sort((a, b) => a.localeCompare(b))
		.map((value) => ({ label: value, value }));
}

/**
 * Return antd column props (filteredValue, onFilter, filterDropdown) for a
 * CheckboxFilterDropdown-based filter.  Spread the result into a column definition.
 */
export function getCheckboxFilterProps<T>(
	filters: string[],
	setFilters: (values: string[]) => void,
	options: CheckboxFilterOption[],
	getRecordValue: (record: T) => string | undefined,
) {
	return {
		filteredValue: filters.length ? filters : null,
		onFilter: (value: unknown, record: T) => {
			if (value === undefined || value === null || value === '') return true;
			const normalizedValue = typeof value === 'string' ? value : String(value);
			return getRecordValue(record) === normalizedValue;
		},
		filterDropdown: ({
			confirm,
			clearFilters,
			selectedKeys,
			setSelectedKeys: setTempKeys,
		}: FilterDropdownProps) => {
			const currentKeys = Array.isArray(selectedKeys) ? (selectedKeys as string[]) : filters;
			return (
				<CheckboxFilterDropdown
					options={options}
					selectedKeys={currentKeys}
					onApply={(keys) => {
						const k = keys.filter(Boolean);
						setFilters(k);
						setTempKeys?.(k);
						confirm({ closeDropdown: true });
					}}
					onReset={() => {
						setFilters([]);
						setTempKeys?.([]);
						clearFilters?.();
						confirm({ closeDropdown: false });
					}}
					onTempKeysChange={(keys) => {
						setTempKeys?.(keys);
					}}
				/>
			);
		},
	};
}
